import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  InfoCircleIcon,
  PlusIcon,
  Trash03Icon,
} from '@langchain/untitled-ui-icons';
import { Autocomplete, LinearProgress } from '@mui/joy';

import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { Button, IconButton, Tooltip } from '@/design-system';
import DragIcon from '@/icons/DragIcon.svg?react';
import { cn } from '@/utils/tailwind';

export type Row = {
  id: string;
  values: Record<string, string | number | boolean>;
  canRemove?: boolean;
  pending?: boolean;
};

export type TableColumn = {
  key: string;
  header: string;
  tooltip?: React.ReactNode;
  renderCell?: (item: Row, index: number) => React.ReactNode;
  width?: string;
};

export type DragAndDropOrderedTableProps = {
  title: string;
  description?: React.ReactNode;
  rows: Row[];
  tableColumns: TableColumn[];
  isLoading?: boolean;
  onChange: (items: Row[]) => void;
  onAddRow?: (value: string) => void;
  onRemoveRow?: (item: Row) => void;
  className?: string;
  placeholder?: string;
  addRowButtonText?: string;
  suggestionOptions?: string[];
  onReset?: () => void;
  hideAddButton?: boolean;
};

function SortableTableRow({
  item,
  tableColumns,
  isNewRow = false,
  placeholder,
  suggestionOptions,
  onRemove,
  onValueChange,
}: {
  item: Row;
  tableColumns: TableColumn[];
  isNewRow?: boolean;
  placeholder?: string;
  suggestionOptions?: string[];
  onRemove?: (item: Row) => void;
  onValueChange?: (item: Row, value: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative' as const,
  };

  const currentValue = (item.values.name as string) || '';
  const canRemoveRow = isNewRow || item.canRemove;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={isNewRow ? 'border-secondary' : 'hover:bg-secondary/50'}
    >
      <TableCell className="py-2">
        <div
          className="flex cursor-grab items-center justify-center active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <DragIcon className={'h-5 w-5 text-tertiary dark:invert'} />
        </div>
      </TableCell>

      {tableColumns.map((column, index) => (
        <TableCell
          className="py-2"
          key={[column.key, isNewRow ? 'new-row' : 'row', item.id].join('-')}
        >
          {isNewRow && index === 0 ? (
            <Autocomplete
              size="sm"
              placeholder={placeholder ?? 'Row name'}
              value={currentValue}
              onInputChange={(_, value) => onValueChange?.(item, value || '')}
              onChange={(_, value) => {
                if (value) {
                  onValueChange?.(item, value);
                }
              }}
              options={suggestionOptions || []}
              freeSolo
              sx={{ minWidth: 200 }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && onRemove) {
                  onRemove(item);
                }
              }}
            />
          ) : column.renderCell ? (
            column.renderCell(item, index)
          ) : (
            item.values[column.key]
          )}
        </TableCell>
      ))}

      <TableCell className="py-2 pr-4 text-right">
        {canRemoveRow && (
          <IconButton
            size="sm"
            color="secondary"
            variant="plain"
            onClick={() => onRemove?.(item)}
            icon={Trash03Icon}
            label="Remove"
          />
        )}
      </TableCell>
    </TableRow>
  );
}

export function DragAndDropOrderedTable({
  title,
  description,
  rows = [],
  tableColumns,
  isLoading,
  onChange,
  onRemoveRow,
  className,
  placeholder,
  addRowButtonText,
  suggestionOptions = [],
  onReset,
  hideAddButton,
}: DragAndDropOrderedTableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper function to identify pending rows by flag
  const isPendingRow = (row: Row) => {
    return row.pending;
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newRows = arrayMove(rows, oldIndex, newIndex);
        onChange(newRows);
      }
    }
  }

  const addNewPendingRow = () => {
    const newRow: Row = {
      id: `new-${Date.now()}-${Math.random()}`,
      values: { name: '' },
      canRemove: true,
      pending: true,
    };
    onChange([newRow, ...rows]);
  };

  const removePendingRow = (item: Row) => {
    const newRows = rows.filter((row) => row.id !== item.id);
    onChange(newRows);
  };

  const updatePendingRowValue = (item: Row, value: string) => {
    const newRows = rows.map((row) =>
      row.id === item.id
        ? { ...row, values: { ...row.values, name: value } }
        : row
    );
    onChange(newRows);
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <div
      className={cn(
        'flex flex-col overflow-auto rounded-md border border-secondary',
        className
      )}
    >
      <div className="flex items-center p-6">
        <div className="mr-2 flex flex-grow flex-col gap-1">
          <h2 className="text-md line-height-1 font-medium">{title}</h2>
          {description && (
            <p className="text-sm text-tertiary">{description}</p>
          )}
        </div>
        <div className="ml-4 flex items-center">
          {onReset && (
            <Button
              size="sm"
              variant="underlined"
              color="secondary"
              onClick={onReset}
              className="mr-2"
            >
              Reset
            </Button>
          )}
          {!hideAddButton && (
            <Button
              color="secondary"
              variant="outlined"
              size="sm"
              leftDecorator={PlusIcon}
              onClick={addNewPendingRow}
            >
              {addRowButtonText ?? 'Add row'}
            </Button>
          )}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table className="w-full border-t border-secondary">
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: '82px', textAlign: 'center' }}>
                Order
              </TableHead>
              {tableColumns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width ? column.width : undefined }}
                >
                  {column.tooltip ? (
                    <div className="flex items-center gap-1">
                      {column.header}
                      <Tooltip title={column.tooltip} side="top">
                        <InfoCircleIcon className="h-4 w-4" />
                      </Tooltip>
                    </div>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
              <TableHead className="w-[64px]"></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            <SortableContext
              items={rows.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              {rows.map((item) => {
                const isPending = isPendingRow(item);
                return (
                  <SortableTableRow
                    key={item.id}
                    item={item}
                    tableColumns={tableColumns}
                    isNewRow={isPending}
                    placeholder={placeholder}
                    suggestionOptions={suggestionOptions}
                    onRemove={isPending ? removePendingRow : onRemoveRow}
                    onValueChange={
                      isPending ? updatePendingRowValue : undefined
                    }
                  />
                );
              })}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </div>
  );
}
